// src/components/ui/Section.tsx
// Section / Field / Metric / Empty — promoted out of
// features/ip-case-sheet/ui.tsx, where the Case Sheet's eleven sections
// first needed them. The Consultation rebuild is the second consumer, which
// is the point at which they stop being one feature's private chrome; see
// the wireframe's §04 component inventory.
//
// How these relate to the two neighbours they are easily confused with:
//
//   Section vs SectionCard — SectionCard is the *lettered, status-bearing*
//   card a long generated form is broken into (Pre-Consult's A/B/C).
//   Section is the plain titled block: a heading, an optional subtitle, an
//   optional header action. Most screens want Section; a screen whose
//   sections are an ordered sequence someone has to work through wants
//   SectionCard.
//
//   Empty vs States.tsx's EmptyState — EmptyState is the whole-screen "this
//   list has nothing in it" panel. Empty is the same statement *inside* a
//   section that is otherwise fine: dashed, shorter, and it does not pretend
//   to be a card of its own.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function Section({
  title, subtitle, action, children, className, focusId,
}: {
  title: string; subtitle?: ReactNode; action?: ReactNode; children: ReactNode; className?: string;
  /** Space-separated `data-focus` tokens — lets a link elsewhere on the
   *  screen scroll this section into view (the IP Case Sheet's drill-through). */
  focusId?: string;
}) {
  return (
    <section data-focus={focusId} className={cn('rounded-xl border border-border bg-card p-4 sm:p-5', className)}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <div className="mt-0.5 text-xs text-ink-muted">{subtitle}</div>}
        </div>
        {action && <div className="flex shrink-0 flex-wrap gap-2">{action}</div>}
      </div>
      {children}
    </section>
  );
}

export function Empty({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border px-4 py-7 text-center">
      <div className="text-[13px] text-ink-muted">{label}</div>
      {hint && <div className="mt-1 text-xs text-ink-muted/75">{hint}</div>}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-0.5 text-[13px] break-words">{children ?? '—'}</div>
    </div>
  );
}

/** Compact label/value strip used across snapshot cards. */
export function Metric({ label, value, tone }: { label: string; value: ReactNode; tone?: 'alert' | 'normal' }) {
  return (
    <div className="min-w-[74px]">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={cn('text-sm font-semibold tabular-nums', tone === 'alert' && 'text-danger-ink')}>{value}</div>
    </div>
  );
}
